import type { Company } from '../lib/companiesApi';
import { isoDateTime } from './company';

// ---------------------------------------------------------------------------
// Multi-company fixtures for Test Mode. Three firms: the default one plus two
// secondary firms so the sidebar switcher and the Financials multi-select have
// something to demo. `testCompanyTag()` deterministically assigns existing
// fixtures (clients, suppliers, projects, invoices) to a firm; NULL = default.
// ---------------------------------------------------------------------------

const CREATED_AT = isoDateTime(-600, 9);
const UPDATED_AT = isoDateTime(-10, 14);

export const TEST_COMPANY_DEFAULT_ID = 'co-1';
export const TEST_COMPANY_2_ID = 'co-2';
export const TEST_COMPANY_3_ID = 'co-3';

export const TEST_COMPANIES: Company[] = [
  {
    id: TEST_COMPANY_DEFAULT_ID,
    legalName: 'Firmio Demo SRL',
    displayName: 'Firmio Demo',
    cui: 'RO47847495',
    regCom: 'J12/100/2019',
    address: 'Str. Exemplu nr. 1',
    city: 'Cluj-Napoca',
    county: 'Cluj',
    country: 'România',
    iban: 'RO49AAAA1B31007593840001',
    bank: 'Banca Transilvania',
    email: 'office@firmio-demo.ro',
    phone: '+40 264 000 001',
    website: 'https://firmio-demo.ro',
    logoUrl: null,
    defaultCurrency: 'RON',
    vatPayer: true,
    isDefault: true,
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  },
  {
    id: TEST_COMPANY_2_ID,
    legalName: 'Firmio Studio SRL',
    displayName: 'Firmio Studio',
    cui: 'RO32101234',
    regCom: 'J12/200/2021',
    address: 'Str. Exemplu nr. 2',
    city: 'Cluj-Napoca',
    county: 'Cluj',
    country: 'România',
    iban: 'RO49AAAA1B31007593840002',
    bank: 'ING Bank',
    email: 'office@firmio-studio.ro',
    phone: '+40 264 000 002',
    website: 'https://firmio-studio.ro',
    logoUrl: null,
    defaultCurrency: 'RON',
    vatPayer: true,
    isDefault: false,
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  },
  {
    id: TEST_COMPANY_3_ID,
    legalName: 'Firmio Labs SRL',
    displayName: 'Firmio Labs',
    cui: 'RO45678901',
    regCom: 'J40/300/2023',
    address: 'Str. Exemplu nr. 3',
    city: 'București',
    county: 'București',
    country: 'România',
    iban: 'RO49AAAA1B31007593840003',
    bank: 'BCR',
    email: 'office@firmio-labs.ro',
    phone: '+40 21 000 0003',
    website: 'https://firmio-labs.ro',
    logoUrl: null,
    defaultCurrency: 'RON',
    vatPayer: false,
    isDefault: false,
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  },
];

export interface CompanyTag {
  companyId: string | null;
  company: { id: string; name: string } | null;
}

function tagFor(companyId: string | null): CompanyTag {
  if (!companyId) return { companyId: null, company: null };
  const c = TEST_COMPANIES.find((x) => x.id === companyId);
  return c
    ? { companyId: c.id, company: { id: c.id, name: c.displayName || c.legalName } }
    : { companyId: null, company: null };
}

/**
 * Deterministic firm tag for a fixture id (e.g. 'c4', 's7', 'pr12'). Most
 * entities stay on the default firm (NULL); every 4th goes to Firmio Studio
 * and every 7th to Firmio Labs, so switching firms visibly filters lists.
 */
export function testCompanyTag(id: string | null | undefined): CompanyTag {
  const n = Number((id ?? '').replace(/\D/g, ''));
  if (!Number.isFinite(n) || n <= 0) return tagFor(null);
  if (n % 4 === 0) return tagFor(TEST_COMPANY_2_ID);
  if (n % 7 === 0) return tagFor(TEST_COMPANY_3_ID);
  return tagFor(null);
}

/**
 * Mock-router helper: keep rows matching the requested firm. NULL companyId
 * counts as the default firm.
 */
export function filterByCompany<T extends { companyId: string | null }>(
  rows: T[],
  companyId: string | null | undefined,
): T[] {
  if (!companyId) return rows;
  if (companyId === TEST_COMPANY_DEFAULT_ID) {
    return rows.filter((r) => r.companyId === null || r.companyId === companyId);
  }
  return rows.filter((r) => r.companyId === companyId);
}
